import React, { useState, useEffect } from 'react';
import { Shield, Filter, Loader2, Trash2, Save, Mail, Clock, User as UserIcon, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeleteConfirmModal } from '@/components/DeleteConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'new', label: 'New', color: 'text-sky-400 bg-sky-400/10' },
  { key: 'under_review', label: 'Under Review', color: 'text-amber-400 bg-amber-400/10' },
  { key: 'actioned', label: 'Actioned', color: 'text-emerald-400 bg-emerald-400/10' },
  { key: 'rejected', label: 'Rejected', color: 'text-red-400 bg-red-400/10' },
];

const statusColor = (s) => STATUSES.find(x => x.key === s)?.color || 'text-white bg-white/5';
const statusLabel = (s) => STATUSES.find(x => x.key === s)?.label || s;

const roleLabel = (r) => ({
  artist: 'Artist',
  rights_owner: 'Rights owner',
  label: 'Label',
  manager: 'Manager',
  other: 'Other',
}[r] || r);

export default function AdminTakedowns() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notesById, setNotesById] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchList = () => {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : '';
    axios.get(`${API}/admin/takedowns${q}`)
      .then(r => {
        setItems(r.data.takedowns || []);
        setCounts(r.data.counts || {});
        const notesMap = {};
        (r.data.takedowns || []).forEach(t => { notesMap[t.id] = t.admin_notes || ''; });
        setNotesById(notesMap);
      })
      .catch(() => toast.error('Failed to load takedowns'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/takedowns/${id}`, { status });
      toast.success(`Marked as ${statusLabel(status)}`);
      fetchList();
    } catch { toast.error('Failed to update status'); }
  };

  const saveNotes = async (id) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/admin/takedowns/${id}`, { admin_notes: notesById[id] || '' });
      toast.success('Notes saved');
    } catch { toast.error('Failed to save notes'); }
    setSavingId(null);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/takedowns/${deleteTarget.id}`);
      toast.success('Takedown deleted');
      setDeleteTarget(null);
      fetchList();
    } catch { toast.error('Failed to delete'); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-takedowns">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
          Takedown Requests
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Content removal / rights takedowns from artists and rights owners</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          data-testid="td-filter-all"
          onClick={() => setStatusFilter('')}
          className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 transition-colors ${!statusFilter ? 'bg-white/10 text-white' : 'bg-white/5 text-[#94A3B8] hover:text-white'}`}
        >
          <Filter size={12} /> All
        </button>
        {STATUSES.map(s => (
          <button
            key={s.key}
            data-testid={`td-filter-${s.key}`}
            onClick={() => setStatusFilter(s.key)}
            className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 transition-colors ${
              statusFilter === s.key ? s.color : 'bg-white/5 text-[#94A3B8] hover:text-white'
            }`}
          >
            {s.label} <span className="opacity-70">{counts[s.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div data-testid="empty-takedowns" className="text-center py-20 bg-[#171A21] rounded-2xl border border-white/5">
          <Shield size={40} className="text-[#94A3B8] mx-auto mb-4" />
          <p className="text-white font-medium">No takedown requests {statusFilter ? `in "${statusLabel(statusFilter)}"` : 'yet'}</p>
          <p className="text-[#94A3B8] text-sm mt-1">Requests from rights owners will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((t) => (
            <div key={t.id} data-testid={`takedown-${t.id}`} className="bg-[#171A21] border border-white/5 rounded-2xl p-5 sm:p-6">
              <div className="flex flex-col lg:flex-row gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                      <Shield size={20} className="text-[#D4AF37]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-white font-semibold truncate">
                          {t.scope === 'blanket' ? 'Blanket exclusion — all works' : (t.work_title || 'Untitled work')}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(t.status)}`}>
                          {statusLabel(t.status)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-white/5 text-[#94A3B8]">
                          {t.scope === 'blanket' ? 'Blanket' : 'Specific'}
                        </span>
                      </div>
                      {t.work_artist && <p className="text-[#94A3B8] text-sm truncate">{t.work_artist}</p>}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#94A3B8] mt-2">
                        <span className="flex items-center gap-1.5"><UserIcon size={12} />{t.name} · {roleLabel(t.role)}</span>
                        <span className="flex items-center gap-1.5"><Mail size={12} />{t.email}</span>
                        {t.phone && <span>Phone: {t.phone}</span>}
                        <span className="flex items-center gap-1.5"><Clock size={12} />{fmtDate(t.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {t.work_links && (
                    <div className="mt-4 pl-14">
                      <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Links</p>
                      <p className="text-sm text-white/80 whitespace-pre-wrap break-all">{t.work_links}</p>
                    </div>
                  )}

                  <div className="mt-4 pl-14">
                    <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Statement of rights & reason</p>
                    <p className="text-sm text-white/80 whitespace-pre-wrap">{t.reason}</p>
                  </div>

                  <div className="mt-4 pl-14 flex items-center gap-2 text-xs text-[#64748B]">
                    <ExternalLink size={12} />
                    Signed by <span className="text-[#94A3B8]">{t.signature}</span> · declaration accepted
                  </div>
                </div>

                <div className="lg:w-72 shrink-0 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Status</label>
                    <select
                      data-testid={`td-status-${t.id}`}
                      value={t.status}
                      onChange={(e) => setStatus(t.id, e.target.value)}
                      className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                    >
                      {STATUSES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Admin notes</label>
                    <textarea
                      data-testid={`td-notes-${t.id}`}
                      rows={3}
                      value={notesById[t.id] ?? ''}
                      onChange={(e) => setNotesById(prev => ({ ...prev, [t.id]: e.target.value }))}
                      placeholder="Internal notes for the team…"
                      className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                    />
                    <button
                      data-testid={`td-save-${t.id}`}
                      onClick={() => saveNotes(t.id)}
                      disabled={savingId === t.id}
                      className="mt-2 w-full bg-white/5 hover:bg-white/10 text-white text-xs font-medium rounded-full py-2 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {savingId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save notes
                    </button>
                  </div>

                  <button
                    data-testid={`td-delete-${t.id}`}
                    onClick={() => setDeleteTarget(t)}
                    className="w-full text-red-400 hover:bg-red-500/10 text-xs font-medium rounded-full py-2 flex items-center justify-center gap-2"
                  >
                    <Trash2 size={12} /> Delete request
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          message={`This will permanently remove the takedown request from ${deleteTarget.name}.`}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
